import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';

const AdminFAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question_tr: '',
    question_en: '',
    answer_tr: '',
    answer_en: '',
    category: '',
    order: 0,
  });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/faq`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingFaq) {
        await axios.put(`${API}/faq/${editingFaq.id}`, formData);
        toast.success('SSS güncellendi!');
      } else {
        await axios.post(`${API}/faq`, formData);
        toast.success('SSS eklendi!');
      }
      fetchFaqs();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Bir hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu SSS\'yi silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${API}/faq/${id}`);
      toast.success('SSS silindi!');
      fetchFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Bir hata oluştu!');
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question_tr: faq.question_tr,
      question_en: faq.question_en,
      answer_tr: faq.answer_tr,
      answer_en: faq.answer_en,
      category: faq.category,
      order: faq.order,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingFaq(null);
    setFormData({
      question_tr: '',
      question_en: '',
      answer_tr: '',
      answer_en: '',
      category: '',
      order: 0,
    });
  };

  const handleChange = (e) => {
    const value = e.target.type === 'number' ? parseInt(e.target.value) : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 
            className="text-3xl font-bold text-gray-900"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="faq-admin-title"
          >
            SSS Yönetimi
          </h1>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-faq-btn">
                <Plus className="w-4 h-4 mr-2" />
                Yeni SSS
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingFaq ? 'SSS Düzenle' : 'Yeni SSS Ekle'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="question_tr">Soru (TR)</Label>
                    <Input
                      id="question_tr"
                      name="question_tr"
                      value={formData.question_tr}
                      onChange={handleChange}
                      required
                      data-testid="faq-question-tr"
                    />
                  </div>
                  <div>
                    <Label htmlFor="question_en">Question (EN)</Label>
                    <Input
                      id="question_en"
                      name="question_en"
                      value={formData.question_en}
                      onChange={handleChange}
                      required
                      data-testid="faq-question-en"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="answer_tr">Cevap (TR)</Label>
                  <Textarea
                    id="answer_tr"
                    name="answer_tr"
                    value={formData.answer_tr}
                    onChange={handleChange}
                    required
                    rows={4}
                    data-testid="faq-answer-tr"
                  />
                </div>
                <div>
                  <Label htmlFor="answer_en">Answer (EN)</Label>
                  <Textarea
                    id="answer_en"
                    name="answer_en"
                    value={formData.answer_en}
                    onChange={handleChange}
                    required
                    rows={4}
                    data-testid="faq-answer-en"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Kategori</Label>
                    <Input
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      data-testid="faq-category"
                    />
                  </div>
                  <div>
                    <Label htmlFor="order">Sıra</Label>
                    <Input
                      id="order"
                      name="order"
                      type="number"
                      value={formData.order}
                      onChange={handleChange}
                      data-testid="faq-order"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full" data-testid="faq-submit">
                  {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-4">
          {faqs.map((faq) => (
            <div key={faq.id} className="bg-white rounded-xl border border-gray-200 p-6" data-testid={`faq-card-${faq.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">{faq.category}</span>
                    <span className="text-xs text-gray-500">Sıra: {faq.order}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.question_tr}</h3>
                  <p className="text-gray-600 text-sm">{faq.answer_tr}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(faq)} data-testid={`edit-faq-${faq.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(faq.id)} data-testid={`delete-faq-${faq.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminFAQ;